import { useState } from 'react';

/**
 * Texto da ASSBAN — cópia literal de `PROHIBITED_CONDUCTS` e `CLOSING_PARAGRAPHS`
 * em `app/(protected)/prova/[logId]/_components/conduct-rules-section.tsx` do
 * assban-cursos-frontend (PM task-mv05zvs652yn). Ao mudar o texto, mude nos dois
 * repos. A ordem é a enviada: as 4 condutas de maior prioridade vêm primeiro.
 */
const PROHIBITED_CONDUCTS = [
  {
    title: 'USAR FONES DE OUVIDO OU OUTROS DISPOSITIVOS ELETRÔNICOS',
    explanation:
      'Não é permitido utilizar fones de ouvido, headset, celular, smartwatch, tablet ou qualquer outro dispositivo eletrônico durante a realização da prova. Também não é permitido utilizar aplicativos, sites, ferramentas de pesquisa ou qualquer recurso externo para consultar informações ou obter auxílio.',
  },
  {
    title: 'FOTOGRAFAR, FILMAR OU GRAVAR A PROVA',
    explanation:
      'É proibido fotografar, filmar, gravar, capturar a tela ou reproduzir, por qualquer meio, as perguntas, respostas, alternativas ou qualquer outro conteúdo apresentado durante a prova. O conteúdo da avaliação é confidencial e não pode ser copiado, armazenado ou compartilhado.',
  },
  {
    title: 'DESVIAR O OLHAR OU PERDER O FOCO DA TELA',
    explanation:
      'Durante toda a prova, mantenha sua atenção na tela. Não é permitido olhar repetidamente para os lados, para baixo, para trás ou permanecer por períodos prolongados olhando para fora da área da prova. Também não é permitido ausentar-se do campo de visão da câmera sem autorização.',
  },
  {
    title: 'LER AS PERGUNTAS OU RESPOSTAS EM VOZ ALTA',
    explanation:
      'Não leia em voz alta as perguntas, alternativas ou respostas da prova. A avaliação deve ser realizada individualmente e em silêncio. Falar durante a prova poderá ser interpretado como tentativa de comunicação ou de compartilhamento do conteúdo da avaliação.',
  },
  {
    title: 'RECEBER AJUDA DE OUTRA PESSOA',
    explanation:
      'A prova é individual. Não é permitido conversar, pedir ajuda, receber orientações ou permitir que outra pessoa participe, responda ou permaneça auxiliando durante a realização da avaliação.',
  },
  {
    title: 'CONSULTAR MATERIAIS OU FAZER ANOTAÇÕES',
    explanation:
      'Não utilize livros, apostilas, anotações, papéis, cadernos, arquivos digitais ou qualquer outro material de consulta. Também não faça anotações sobre perguntas ou respostas da prova.',
  },
  {
    title: 'ABRIR OUTROS SITES, PROGRAMAS OU JANELAS',
    explanation:
      'Não abra outras abas do navegador, programas, aplicativos ou documentos durante a prova. Também não minimize ou saia da tela da avaliação. Essas ações poderão ser identificadas pelo sistema de monitoramento.',
  },
  {
    title: 'DESLIGAR, COBRIR OU IMPEDIR O USO DA CÂMERA E DO MICROFONE',
    explanation:
      'A câmera e o microfone devem permanecer ligados e funcionando durante toda a prova. Não cubra a câmera, não bloqueie sua imagem e não interrompa propositalmente o áudio ou o vídeo.',
  },
  {
    title: 'SAIR DO LOCAL OU DO CAMPO DE VISÃO DA CÂMERA',
    explanation:
      'Permaneça no local da prova e visível pela câmera durante toda a avaliação. Evite levantar-se, afastar-se ou sair do enquadramento sem necessidade ou autorização.',
  },
  {
    title: 'RECUSAR A VERIFICAÇÃO DE IDENTIDADE',
    explanation:
      'Quando solicitado, siga as orientações para confirmação da sua identidade e para verificação das condições de realização da prova.',
  },
  {
    title: 'COMPARTILHAR O CONTEÚDO DA PROVA',
    explanation:
      'Não divulgue, envie, publique ou compartilhe perguntas, respostas ou qualquer conteúdo da avaliação antes, durante ou depois da prova.',
  },
] as const;

const CLOSING_PARAGRAPHS = [
  'Para fins de segurança e auditoria, o candidato deverá manter a câmera e o microfone habilitados durante todo o exame, autorizando a captura e a transmissão de áudio e vídeo, exclusivamente para monitoramento e verificação da regularidade da avaliação.',
  'O descumprimento de qualquer das regras estabelecidas autoriza a ASSBAN a impedir a participação do candidato, interromper a realização do exame, suspender ou reter a divulgação do resultado até a conclusão da análise de autenticidade, bem como desclassificar o candidato caso sejam constatadas irregularidades, fraudes ou qualquer ação que comprometa a integridade do processo de certificação.',
] as const;

const SECTION_TITLE_ID = 'conduct-rules-title';
const CONDUCTS_LEAD_ID = 'conduct-rules-lead';

const ALL_POSITIONS = PROHIBITED_CONDUCTS.map((_, index) => index + 1);

/** Anel de foco de todos os gatilhos (condutas e "Expandir todos"): só no foco por teclado. */
const TRIGGER_FOCUS_CLASS =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2';

/** Chevron decorativo: quem anuncia o estado é o `aria-expanded` do gatilho. */
function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      className={`mt-0.5 shrink-0 text-red-600 transition-transform duration-200 ${
        open ? 'rotate-180' : ''
      }`}
    >
      <path
        d="M5 7.5L10 12.5L15 7.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Seção "Condutas proibidas durante a prova" da etapa de requisitos.
 *
 * Cada conduta é um acordeão: o `<button>` alterna o painel da explicação, que
 * é o irmão seguinte do gatilho e abre no fluxo do documento. Não há handler de
 * mouse. Várias condutas podem ficar abertas ao mesmo tempo (daí o `Set`).
 *
 * O painel existe sempre no DOM e usa `hidden` quando fechado, para o
 * `aria-controls` nunca apontar para um `id` inexistente. Sem `max-h`/`overflow`:
 * uma caixa rolável não focável deixaria texto inalcançável por teclado.
 */
export function ConductRulesSection() {
  const [openPositions, setOpenPositions] = useState<Set<number>>(
    () => new Set(),
  );

  const allOpen = openPositions.size === PROHIBITED_CONDUCTS.length;

  const toggleConduct = (position: number) =>
    setOpenPositions(current => {
      const next = new Set(current);
      if (next.has(position)) {
        next.delete(position);
      } else {
        next.add(position);
      }
      return next;
    });

  const toggleAll = () =>
    setOpenPositions(current =>
      current.size === PROHIBITED_CONDUCTS.length
        ? new Set()
        : new Set(ALL_POSITIONS),
    );

  return (
    <section
      aria-labelledby={SECTION_TITLE_ID}
      className="rounded-2xl border border-slate-200 bg-slate-50 p-5"
    >
      <h2
        id={SECTION_TITLE_ID}
        className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500"
      >
        Condutas proibidas durante a prova
      </h2>

      <div className="mt-4 space-y-3 text-sm text-slate-700">
        <p className="rounded-xl bg-red-600 px-4 py-3 text-white">
          <strong className="font-bold">ATENÇÃO:</strong> O descumprimento de
          qualquer uma das regras abaixo poderá resultar na{' '}
          <strong className="font-bold">REPROVAÇÃO IMEDIATA</strong> e no
          encerramento da prova.
        </p>

        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <p id={CONDUCTS_LEAD_ID} className="font-semibold text-red-900">
              É PROIBIDO DURANTE A PROVA:
            </p>
            <button
              type="button"
              onClick={toggleAll}
              className={`${TRIGGER_FOCUS_CLASS} shrink-0 rounded text-xs font-semibold text-red-700 underline underline-offset-2`}
            >
              {allOpen ? 'Recolher todos' : 'Expandir todos'}
            </button>
          </div>

          {/* A chamada dá o nome acessível à lista: a tela tem outra lista
              logo abaixo ("Ambiente recomendado"). */}
          <ul
            aria-labelledby={CONDUCTS_LEAD_ID}
            className="mt-1 divide-y divide-red-100"
          >
            {PROHIBITED_CONDUCTS.map((conduct, index) => {
              const position = index + 1;
              const isOpen = openPositions.has(position);
              const panelId = `conduct-rule-explanation-${position}`;

              return (
                <li key={conduct.title}>
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => toggleConduct(position)}
                    className={`${TRIGGER_FOCUS_CLASS} flex w-full items-start justify-between gap-3 rounded py-2.5 text-left`}
                  >
                    <span className="border-l-2 border-red-400 pl-3 text-xs font-bold uppercase leading-5 text-red-900">
                      {position}. {conduct.title}
                    </span>
                    <ChevronIcon open={isOpen} />
                  </button>

                  <div
                    id={panelId}
                    hidden={!isOpen}
                    className="pb-3 pl-3 text-sm leading-6 text-slate-700"
                  >
                    {conduct.explanation}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {CLOSING_PARAGRAPHS.map(paragraph => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>
    </section>
  );
}
